import React, { ReactElement, useContext } from 'react';
import { useQuery } from '@apollo/client';
import TrackDispatchInstruction from '../../../graphql/queries/sales/TrackDispatchInstructionQuery.graphql';
import MaxoptraOrderExecutionQuery from '../../../graphql/queries/sales/MaxoptraOrderExecutionQuery.graphql';
import MaxoptraOrderAttachmentsQuery from '../../../graphql/queries/sales/MaxoptraOrderAttachmentsQuery.graphql';
import MaxoptraOrderTrackingQuery from '../../../graphql/queries/sales/MaxoptraOrderTrackingQuery.graphql';
import {
  DeliveryStatus,
  IDispatchInstruction,
  IMaxoptraOrderAttachmentsQueryResult,
  IMaxoptraOrderAttachmentsQueryVariables,
  IMaxoptraOrderExecutionQueryResult,
  IMaxoptraOrderExecutionQueryVariables,
  IMaxoptraOrdersQueryResult,
  IMaxoptraOrderTrackingQueryResult,
  IMaxoptraOrderTrackingQueryVariables,
  ITrackDispatchInstructionQueryResult,
  ITrackDispatchInstructionQueryVariables,
  ITrackingHistory,
  ITrackingResult,
  Maybe,
} from '../../../graphql/schema/p4types';
import { BlurTextField, DateField } from '../../../components/connectedForm/inputComponents';
import Icon from '@mdi/react';
import { mdiGoogleMaps } from '@mdi/js';
import ColumnLabel from '../../../components/connectedForm/ColumnLabel';
import { default as Form } from '../../../components/connectedForm';
import * as StatusPill from '../../../components/StatusPill';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import { useFullUserDetails } from '../../../utils/hooks';

const DATE_TIME_FORMAT = 'iii do MMM yyyy HH:mm';

export const Status = ({ deliveryStatus }: { deliveryStatus: Maybe<DeliveryStatus> }): JSX.Element | null => {
  if (!deliveryStatus) {
    return null;
  }

  let content;

  const text = deliveryStatus.replace(/_/g, ' ');

  switch (deliveryStatus) {
    case DeliveryStatus.PreTransit:
    case DeliveryStatus.InTransit:
    case DeliveryStatus.OutForDelivery:
      content = <StatusPill.WarningPill>{text}</StatusPill.WarningPill>;
      break;
    case DeliveryStatus.Delivered:
    case DeliveryStatus.AvailableForCollection:
      content = <StatusPill.SuccessPill>{text}</StatusPill.SuccessPill>;
      break;
    case DeliveryStatus.ReturnToSender:
    case DeliveryStatus.Cancelled:
    case DeliveryStatus.Failure:
    case DeliveryStatus.Error:
      content = <StatusPill.DangerPill>{text}</StatusPill.DangerPill>;
      break;
    default:
      content = <StatusPill.DefaultPill>{text}</StatusPill.DefaultPill>;
  }

  return content;
};

export const AttachmentsDetails = ({ orderReference }: { orderReference: string }) => {
  const fullUserDetails = useFullUserDetails();
  const handleQueryError = useComponentQueryErrorHandling();

  const { loading: attachmentsLoading, data: attachmentsData } = useQuery<
    IMaxoptraOrderAttachmentsQueryResult,
    IMaxoptraOrderAttachmentsQueryVariables
  >(MaxoptraOrderAttachmentsQuery, {
    fetchPolicy: 'network-only',
    variables: {
      orderReference: orderReference,
    },
    skip: !fullUserDetails?.flags?.maxoptraEnabled,
    onError: handleQueryError,
  });

  if (!fullUserDetails?.flags?.maxoptraEnabled) {
    return null;
  }

  if (attachmentsLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  const orderAttachments = attachmentsData?.data;

  return (
    <>
      <Form.Legend label="Maxoptra Attachments" />
      {!orderAttachments || orderAttachments.length === 0 ? (
        <div>None to display</div>
      ) : (
        <Form.Row>
          {orderAttachments.map((orderAttachment) => {
            if (!orderAttachment) {
              return null;
            }

            return (
              <Form.Fieldset cols={1 / 3} key={orderAttachment.attachmentReferenceNumber!}>
                <div style={{ marginBottom: '1em' }}>
                  <a href={orderAttachment.imageFull!} target="_blank">
                    <img
                      src={orderAttachment.imageSmall!}
                      alt={orderAttachment.comment!}
                      style={{ maxWidth: '100%' }}
                    />
                  </a>
                </div>
              </Form.Fieldset>
            );
          })}
        </Form.Row>
      )}
    </>
  );
};

export const ExecutionDetails = ({ orderReference }: { orderReference: string }) => {
  const fullUserDetails = useFullUserDetails();
  const handleQueryError = useComponentQueryErrorHandling();

  const { loading: executionLoading, data: executionData } = useQuery<
    IMaxoptraOrderExecutionQueryResult,
    IMaxoptraOrderExecutionQueryVariables
  >(MaxoptraOrderExecutionQuery, {
    fetchPolicy: 'network-only',
    variables: { orderReference },
    skip: !fullUserDetails?.flags?.maxoptraEnabled,
    onError: handleQueryError,
  });

  if (!fullUserDetails?.flags?.maxoptraEnabled) return null;
  if (executionLoading) return <Spinner size={SpinnerSize.large} />;

  const executionDetails = executionData?.data;

  return (
    <>
      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <BlurTextField
            data-testid="maxoptra-order-reference"
            label="Reference"
            readOnly
            value={executionDetails?.orderReferenceNumber}
          />
          <BlurTextField data-testid="maxoptra-order-status" label="Status" readOnly value={executionDetails?.status} />
        </Form.Fieldset>
      </Form.Row>
      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend label="Assignment and Run Information" />
          <BlurTextField
            data-testid="maxoptra-order-assigned-driver-reference"
            label="Driver Reference"
            readOnly
            value={executionDetails?.assignedDriverReference}
          />
          <BlurTextField
            data-testid="maxoptra-order-assigned-driver-name"
            label="Driver Name"
            readOnly
            value={executionDetails?.assignedDriverName}
          />
          <BlurTextField
            data-testid="maxoptra-order-assigned-vehicle-reference"
            label="Vehicle Reference"
            readOnly
            value={executionDetails?.assignedVehicleReference}
          />
          <BlurTextField
            data-testid="maxoptra-order-assigned-vehicle-name"
            label="Vehicle Name"
            readOnly
            value={executionDetails?.assignedVehicleName}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend label="Run and Stop Details" />
          <BlurTextField
            data-testid="maxoptra-order-run-number"
            label="Run Number"
            readOnly
            value={executionDetails?.runNumber?.toString()}
          />
          <BlurTextField
            data-testid="maxoptra-order-run-reference"
            label="Run Reference"
            readOnly
            value={executionDetails?.runReference}
          />
          <BlurTextField
            data-testid="maxoptra-order-stop-number"
            label="Stop Number"
            readOnly
            value={executionDetails?.stopNumber?.toString()}
          />
          <BlurTextField
            data-testid="maxoptra-order-total-stops-in-run"
            label="Total Stops in Run"
            readOnly
            value={executionDetails?.totalStopsInRun?.toString()}
          />
        </Form.Fieldset>
      </Form.Row>

      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend label="Planned Times" />
          <DateField
            data-testid="maxoptra-order-planned-arrival-time"
            label="Planned Arrival Time"
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.plannedArrivalTime}
          />
          <DateField
            data-testid="maxoptra-order-planned-completion-time"
            label="Planned Completion Time"
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.plannedCompletionTime}
          />
          <DateField
            data-testid="maxoptra-eta"
            label="ETA"
            reformatLabel={false}
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.eta}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend label="Actual Times" />
          <DateField
            data-testid="maxoptra-fact-arrival-time-gps"
            label="Arrival Time GPS"
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.factArrivalTimeGps}
          />
          <DateField
            data-testid="maxoptra-fact-completion-time-gps"
            label="Completion Time GPS"
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.factCompletionTimeGps}
          />
          <DateField
            data-testid="maxoptra-fact-arrival-time-reported-time"
            label="Arrival Time Reported"
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.factArrivalTimeReported}
          />
          <DateField
            data-testid="maxoptra-fact-completion-time-reported-time"
            label="Completion Time Reported"
            readOnly
            dateFormat={DATE_TIME_FORMAT}
            value={executionDetails?.factCompletionTimeReported}
          />
        </Form.Fieldset>
      </Form.Row>

      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend label="Failure Information" />
          <BlurTextField
            data-testid="maxoptra-order-fail-reason"
            label="Failure Reason"
            readOnly
            value={executionDetails?.failReason}
          />
          <BlurTextField
            data-testid="maxoptra-order-fail-comment"
            label="Failure Comment"
            readOnly
            value={executionDetails?.failComment}
          />
        </Form.Fieldset>
      </Form.Row>
    </>
  );
};

const TrackingDetailsDisplay = ({ details }: { details: ITrackingResult }) => {
  let content: JSX.Element;

  if (!details) {
    return <div>No tracking details available</div>;
  }

  const trackingDetailsElements: Array<ReactElement> = [];
  if (details.trackingHistories) {
    const trackingHistories = details.trackingHistories as Array<ITrackingHistory>;
    for (let i = 0; i < trackingHistories.length; i++) {
      const trackingDetail = trackingHistories[i];
      trackingDetailsElements.push(
        <Form.Row key={i}>
          <Form.Fieldset cols={2 / 12}>
            <DateField
              readOnly
              includeLabel={false}
              value={trackingDetail.dateTime.substring(0, trackingDetail.dateTime.length - 3)}
              dateFormat={DATE_TIME_FORMAT}
              data-testid="tracking-history-date"
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 12}>
            <Status deliveryStatus={trackingDetail.deliveryStatus} />
          </Form.Fieldset>
          <Form.Fieldset cols={4 / 12}>
            <BlurTextField
              readOnly
              includeLabel={false}
              value={
                trackingDetail.city && trackingDetail.country
                  ? `${trackingDetail.city}, ${trackingDetail.country}`
                  : null
              }
              data-testid="tracking-history-city"
            />
          </Form.Fieldset>
          <Form.Fieldset cols={5 / 12}>
            <BlurTextField
              readOnly
              includeLabel={false}
              value={trackingDetail.description}
              data-testid="tracking-history-description"
            />
          </Form.Fieldset>
        </Form.Row>
      );
    }
  }

  content = (
    <>
      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <div style={{ marginBottom: '1em' }}>
            <Status deliveryStatus={details.deliveryStatus} />
          </div>
          {details.latitude && details.longitude ? (
            <div style={{ padding: '1em 0' }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${details.latitude},${details.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon path={mdiGoogleMaps} size={1} /> Location in Google Maps
              </a>
            </div>
          ) : null}
          {details.estimatedDeliveryDate ? (
            <BlurTextField
              label="Estimated Delivery Date"
              readOnly
              value={details.estimatedDeliveryDate}
              data-testid="estimated-delivery-date"
            />
          ) : null}
          {details.signedBy ? (
            <BlurTextField label="Signed By" readOnly value={details.signedBy} data-testid="signed-by" />
          ) : null}
          {details.signedAt ? (
            <DateField
              label="Signed At"
              readOnly
              dateFormat={DATE_TIME_FORMAT}
              value={details.signedAt}
              data-testid="signed-at"
            />
          ) : null}
          {details.signatureCapture ? (
            <>
              <Form.Legend label="Signature" />
              <img src={details.signatureCapture} alt="Signature" style={{ maxWidth: '50%' }} />
            </>
          ) : null}
        </Form.Fieldset>
      </Form.Row>
      {trackingDetailsElements.length > 1 ? (
        <Form.Row>
          <Form.Fieldset cols={2 / 12}>
            <ColumnLabel label="Date" />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 12}>
            <ColumnLabel label="Status" />
          </Form.Fieldset>
          <Form.Fieldset cols={4 / 12}>
            <ColumnLabel label="Location" />
          </Form.Fieldset>
          <Form.Fieldset cols={5 / 12}>
            <ColumnLabel label="Description" />
          </Form.Fieldset>
        </Form.Row>
      ) : null}

      {trackingDetailsElements}
    </>
  );

  return (
    <>
      <Form.Legend label="Tracking Details" />
      {content}
    </>
  );
};

export const TrackingDetailsForDispatchInstruction = ({
  dispatchInstruction: { id, maxoptraOrder },
}: {
  dispatchInstruction: IDispatchInstruction;
}) => {
  const handleQueryError = useComponentQueryErrorHandling();
  const { loading, data } = useQuery<ITrackDispatchInstructionQueryResult, ITrackDispatchInstructionQueryVariables>(
    TrackDispatchInstruction,
    {
      fetchPolicy: 'network-only',
      variables: {
        dispatchInstructionId: id,
      },
      onError: handleQueryError,
    }
  );

  if (loading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  return (
    <>
      <TrackingDetailsDisplay details={data?.data as ITrackingResult} />
      {maxoptraOrder?.reference ? (
        <>
          <ExecutionDetails orderReference={maxoptraOrder.reference} />
          <AttachmentsDetails orderReference={maxoptraOrder.reference} />
        </>
      ) : null}
    </>
  );
};

export const TrackingDetailsForOrderReference = ({
  orderReference,
  maxoptraOrdersRefetchQuery,
}: {
  orderReference: string;
  maxoptraOrdersRefetchQuery?: () => Promise<IMaxoptraOrdersQueryResult>;
}) => {
  const fullUserDetails = useFullUserDetails();
  const handleQueryError = useComponentQueryErrorHandling();

  const { loading: trackingLoading, data: trackingData } = useQuery<
    IMaxoptraOrderTrackingQueryResult,
    IMaxoptraOrderTrackingQueryVariables
  >(MaxoptraOrderTrackingQuery, {
    fetchPolicy: 'network-only',
    variables: { orderReference },
    skip: !fullUserDetails?.flags?.maxoptraEnabled,
    onError: handleQueryError,
    onCompleted: () => {
      maxoptraOrdersRefetchQuery?.();
    },
  });

  if (!fullUserDetails?.flags?.maxoptraEnabled) {
    return null;
  }

  if (trackingLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  const deleted = (trackingData?.data?.deliveryStatus ?? false) === DeliveryStatus.Cancelled;

  return (
    <>
      <TrackingDetailsDisplay details={trackingData?.data as ITrackingResult} />
      {!deleted && (
        <>
          <ExecutionDetails orderReference={orderReference} />
          <AttachmentsDetails orderReference={orderReference} />
        </>
      )}
    </>
  );
};
